import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOT_LABEL = { full: "All day", am: "Morning", pm: "Afternoon" };

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function AdminCalendar({ bookings = [], blockedDates = [], periods = [], onPickDate }) {
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const { year, month } = cursor;

  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  const todayIso = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  const move = (dir) =>
    setCursor(({ year, month }) => {
      const m = month + dir;
      return { year: year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
    });

  const visibleBookings = bookings.filter((b) => b.status !== "cancelled");

  return (
    <section className="mt-12 rounded-2xl border border-ink/15 bg-white p-6 sm:p-8" data-testid="admin-calendar">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl tracking-tight">
          {MONTHS[month]} <span className="text-ink/40">{year}</span>
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} data-testid="cal-prev" aria-label="Previous month"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/20 text-ink/60 transition-colors duration-300 hover:border-terra hover:text-terra">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setCursor({ year: now.getFullYear(), month: now.getMonth() })} data-testid="cal-today"
            className="rounded-full border border-ink/20 px-4 py-2 text-sm uppercase tracking-[0.15em] text-ink/60 transition-colors duration-300 hover:border-terra hover:text-terra">
            Today
          </button>
          <button onClick={() => move(1)} data-testid="cal-next" aria-label="Next month"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/20 text-ink/60 transition-colors duration-300 hover:border-terra hover:text-terra">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-ink/10 bg-ink/10">
        {WEEKDAYS.map((w) => (
          <div key={w} className="bg-ink/[0.03] px-2 py-2 text-center text-sm uppercase tracking-[0.2em] text-ink/45">
            {w}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} className="min-h-[72px] bg-paper/60 sm:min-h-[104px]" />;
          const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
          const dayBookings = visibleBookings.filter((b) => b.preferred_date === iso);
          const dayBlocks = blockedDates.filter((e) => e.date === iso);
          const isPast = iso < todayIso;
          const outOfSeason = periods.length > 0 && !periods.some((p) => p.start <= iso && iso <= p.end);
          return (
            <button
              key={iso}
              onClick={() => onPickDate && onPickDate(iso)}
              data-testid={`cal-day-${iso}`}
              className={`min-h-[72px] px-1.5 py-1.5 text-left align-top transition-colors duration-200 hover:bg-olive-pale/50 sm:min-h-[104px] sm:px-2.5 sm:py-2 ${outOfSeason ? "bg-white [background-image:repeating-linear-gradient(135deg,transparent,transparent_6px,rgba(28,25,23,0.05)_6px,rgba(28,25,23,0.05)_8px)]" : "bg-white"} ${isPast ? "opacity-45" : ""}`}
            >
              <span className={`text-sm ${iso === todayIso ? "flex h-6 w-6 items-center justify-center rounded-full bg-terra font-semibold text-paper" : outOfSeason ? "font-medium text-ink/30" : "font-medium text-ink/70"}`}>
                {day}
              </span>
              <span className="mt-1 flex flex-col gap-1">
                {dayBlocks.map((e) => (
                  <span key={`${e.date}-${e.slot}`} className="block truncate rounded-md bg-red-50 px-1.5 py-0.5 text-sm font-medium text-red-700">
                    {SLOT_LABEL[e.slot] || "All day"} off
                  </span>
                ))}
                {dayBookings.map((b) => (
                  <span
                    key={b.id}
                    title={`${b.name} — ${b.hike_name} (${b.status})`}
                    className={`block truncate rounded-md px-1.5 py-0.5 text-sm font-medium ${
                      b.status === "confirmed" ? "bg-olive-pale text-olive" : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {b.name.split(" ")[0]} · {b.people}p
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm uppercase tracking-[0.15em] text-ink/50" data-testid="cal-legend">
        <span className="flex items-center gap-2"><span className="h-3.5 w-3.5 rounded-md bg-olive-pale ring-1 ring-olive/40" /> Confirmed</span>
        <span className="flex items-center gap-2"><span className="h-3.5 w-3.5 rounded-md bg-amber-100 ring-1 ring-amber-300" /> Pending</span>
        <span className="flex items-center gap-2"><span className="flex h-3.5 w-3.5 items-center justify-center rounded-md bg-red-50 ring-1 ring-red-200"><span className="h-px w-2.5 bg-red-400" /></span> Taken / day off</span>
        <span className="flex items-center gap-2"><span className="h-3.5 w-3.5 rounded-md ring-1 ring-ink/10 [background-image:repeating-linear-gradient(135deg,transparent,transparent_2px,rgba(28,25,23,0.15)_2px,rgba(28,25,23,0.15)_3px)]" /> Not operating</span>
        <span className="text-ink/35 normal-case tracking-normal">Click any day to prefill the block form below</span>
      </div>
    </section>
  );
}
